import clsx from "clsx";
import type { KeyboardEvent, ReactNode, SVGProps } from "react";
import { Icon } from "../../Icon";
import { Button, Pill } from "../../ui";
import type { PuzzleInstance } from "../../../puzzles/core/framework";
import type { ValidationResult } from "../../../puzzles/core/types";

export interface Point {
  x: number;
  y: number;
}

export interface GridBounds {
  xMin: number;
  xMax: number;
  yMin: number;
  yMax: number;
}

export interface GridProjection {
  bounds: GridBounds;
  project(point: Point): Point;
  unproject(point: Point): Point;
  clamp(point: Point): Point;
  toPath(points: Point[]): string;
}

export type VisualFlags = {
  showGrid?: boolean;
  showAxes?: boolean;
  showLabels?: boolean;
  showHints?: boolean;
  showVectors?: boolean;
  animate?: boolean;
  highContrast?: boolean;
};

const svgSize = 100;
const padding = 8;
const plotSize = svgSize - padding * 2;

export function getVisualFlags(
  visualConfig?: Record<string, unknown>,
): Required<VisualFlags> {
  return {
    showGrid: visualConfig?.showGrid !== false,
    showAxes: visualConfig?.showAxes !== false,
    showLabels: visualConfig?.showLabels !== false,
    showHints: visualConfig?.showHints !== false,
    showVectors: visualConfig?.showVectors !== false,
    animate: visualConfig?.animate !== false,
    highContrast: visualConfig?.highContrast === true,
  };
}

export function boundsFromVisual(
  visual: PuzzleInstance["visual"],
  fallback: GridBounds = { xMin: 0, xMax: 10, yMin: 0, yMax: 10 },
): GridBounds {
  if (visual.width && visual.height) {
    return { xMin: 0, xMax: visual.width - 1, yMin: 0, yMax: visual.height - 1 };
  }
  return fallback;
}

export function GridCanvas({
  bounds,
  selectedPoint,
  onPointSelect,
  disabled = false,
  step = 1,
  ariaLabel,
  visualConfig,
  className,
  children,
}: {
  bounds: GridBounds;
  selectedPoint?: Point;
  onPointSelect?: (point: Point) => void;
  disabled?: boolean;
  step?: number;
  ariaLabel: string;
  visualConfig?: Record<string, unknown>;
  className?: string;
  children?: ReactNode | ((projection: GridProjection) => ReactNode);
}) {
  const flags = getVisualFlags(visualConfig);
  const projection = createProjection(bounds);

  const selectFromClientPoint = (clientX: number, clientY: number, target: SVGSVGElement) => {
    if (!onPointSelect || disabled) return;
    const rect = target.getBoundingClientRect();
    const point = projection.unproject({
      x: ((clientX - rect.left) / rect.width) * svgSize,
      y: ((clientY - rect.top) / rect.height) * svgSize,
    });
    onPointSelect(projection.clamp({ x: Math.round(point.x / step) * step, y: Math.round(point.y / step) * step }));
  };

  const moveSelected = (event: KeyboardEvent<SVGSVGElement>) => {
    if (!onPointSelect || disabled || !selectedPoint) return;
    const delta: Point =
      event.key === "ArrowLeft"
        ? { x: -step, y: 0 }
        : event.key === "ArrowRight"
          ? { x: step, y: 0 }
          : event.key === "ArrowUp"
            ? { x: 0, y: step }
            : event.key === "ArrowDown"
              ? { x: 0, y: -step }
              : { x: 0, y: 0 };
    if (delta.x === 0 && delta.y === 0) return;
    event.preventDefault();
    onPointSelect(projection.clamp({ x: selectedPoint.x + delta.x, y: selectedPoint.y + delta.y }));
  };

  return (
    <svg
      viewBox={`0 0 ${svgSize} ${svgSize}`}
      role={onPointSelect ? "application" : "img"}
      aria-label={ariaLabel}
      tabIndex={onPointSelect && !disabled ? 0 : undefined}
      className={clsx(
        "aspect-square w-full rounded-xl border border-slate-800 bg-slate-950 outline-none ring-cyan-300/0 transition focus:ring-2",
        flags.highContrast && "border-cyan-300 bg-black",
        disabled && "opacity-70",
        className,
      )}
      onPointerDown={(event) => selectFromClientPoint(event.clientX, event.clientY, event.currentTarget)}
      onKeyDown={moveSelected}
    >
      <defs>
        <marker id="puzzle-arrow" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto" markerUnits="strokeWidth">
          <path d="M0,0 L6,3 L0,6 Z" fill="currentColor" />
        </marker>
      </defs>
      {flags.showGrid && <GridLines bounds={bounds} projection={projection} showLabels={flags.showLabels} />}
      {flags.showAxes && <AxisRenderer bounds={bounds} projection={projection} />}
      {typeof children === "function" ? children(projection) : children}
    </svg>
  );
}

export function AxisRenderer({
  bounds,
  projection,
}: {
  bounds: GridBounds;
  projection: GridProjection;
}) {
  const xAxis = bounds.yMin <= 0 && bounds.yMax >= 0 ? 0 : bounds.yMin;
  const yAxis = bounds.xMin <= 0 && bounds.xMax >= 0 ? 0 : bounds.xMin;
  const left = projection.project({ x: bounds.xMin, y: xAxis });
  const right = projection.project({ x: bounds.xMax, y: xAxis });
  const bottom = projection.project({ x: yAxis, y: bounds.yMin });
  const top = projection.project({ x: yAxis, y: bounds.yMax });

  return (
    <g className="text-slate-500">
      <line x1={left.x} y1={left.y} x2={right.x} y2={right.y} stroke="currentColor" strokeWidth="0.55" />
      <line x1={bottom.x} y1={bottom.y} x2={top.x} y2={top.y} stroke="currentColor" strokeWidth="0.55" />
    </g>
  );
}

export function VectorArrow({
  from,
  to,
  projection,
  color = "text-cyan-300",
  label,
  dashed = false,
  width = 1.4,
}: {
  from: Point;
  to: Point;
  projection: GridProjection;
  color?: string;
  label?: string;
  dashed?: boolean;
  width?: number;
}) {
  const start = projection.project(from);
  const end = projection.project(to);
  const midpoint = { x: (start.x + end.x) / 2, y: (start.y + end.y) / 2 };
  return (
    <g className={color}>
      <line
        x1={start.x}
        y1={start.y}
        x2={end.x}
        y2={end.y}
        stroke="currentColor"
        strokeWidth={width}
        strokeDasharray={dashed ? "2 2" : undefined}
        markerEnd="url(#puzzle-arrow)"
      />
      {label && (
        <text x={midpoint.x + 1} y={midpoint.y - 1} fill="currentColor" fontSize="3.5" fontWeight="700">
          {label}
        </text>
      )}
    </g>
  );
}

export function PointMarker({
  point,
  projection,
  label,
  tone = "cyan",
  radius = 1.8,
}: {
  point: Point;
  projection: GridProjection;
  label?: string;
  tone?: "cyan" | "emerald" | "amber" | "rose" | "violet" | "slate";
  radius?: number;
}) {
  const projected = projection.project(point);
  const colors = {
    cyan: "fill-cyan-300 stroke-cyan-950",
    emerald: "fill-emerald-300 stroke-emerald-950",
    amber: "fill-amber-300 stroke-amber-950",
    rose: "fill-rose-300 stroke-rose-950",
    violet: "fill-violet-300 stroke-violet-950",
    slate: "fill-slate-300 stroke-slate-950",
  };
  return (
    <g>
      <circle cx={projected.x} cy={projected.y} r={radius} className={colors[tone]} strokeWidth="0.55" />
      {label && (
        <text x={projected.x + 2.4} y={projected.y - 2.4} className="fill-white" fontSize="3.7" fontWeight="800">
          {label}
        </text>
      )}
    </g>
  );
}

export type ShapeDefinition =
  | { type: "circle"; center: Point; radius: number; tone?: ShapeTone; label?: string }
  | { type: "box"; minX: number; maxX: number; minY: number; maxY: number; tone?: ShapeTone; label?: string }
  | { type: "line"; from: Point; to: Point; tone?: ShapeTone; label?: string }
  | { type: "polyline"; points: Point[]; tone?: ShapeTone; label?: string };

type ShapeTone = "cyan" | "emerald" | "amber" | "rose" | "violet" | "slate";

export function ShapeOverlay({
  shapes,
  projection,
}: {
  shapes: ShapeDefinition[];
  projection: GridProjection;
}) {
  return (
    <g>
      {shapes.map((shape, index) => (
        <ShapeItem key={`${shape.type}-${index}`} shape={shape} projection={projection} />
      ))}
    </g>
  );
}

export function AngleArc({
  origin,
  radius,
  startDegrees,
  endDegrees,
  projection,
  label,
}: {
  origin: Point;
  radius: number;
  startDegrees: number;
  endDegrees: number;
  projection: GridProjection;
  label?: string;
}) {
  const start = polarPoint(origin, radius, startDegrees);
  const end = polarPoint(origin, radius, endDegrees);
  const startProjected = projection.project(start);
  const endProjected = projection.project(end);
  const largeArc = Math.abs(endDegrees - startDegrees) > 180 ? 1 : 0;
  const sweep = endDegrees >= startDegrees ? 0 : 1;
  const middle = projection.project(polarPoint(origin, radius + 0.5, (startDegrees + endDegrees) / 2));
  return (
    <g className="text-amber-300">
      <path
        d={`M ${startProjected.x} ${startProjected.y} A ${radius * 4} ${radius * 4} 0 ${largeArc} ${sweep} ${endProjected.x} ${endProjected.y}`}
        fill="none"
        stroke="currentColor"
        strokeWidth="1"
      />
      {label && (
        <text x={middle.x} y={middle.y} fill="currentColor" fontSize="3.6" fontWeight="800">
          {label}
        </text>
      )}
    </g>
  );
}

export function MatrixEditor({
  label,
  values,
  rows,
  columns,
  disabled,
  onChange,
}: {
  label: string;
  values: number[];
  rows: number;
  columns: number;
  disabled?: boolean;
  onChange(values: number[]): void;
}) {
  return (
    <fieldset className="rounded-xl border border-slate-800 bg-slate-950/55 p-3">
      <legend className="px-1 text-xs font-bold text-slate-300">{label}</legend>
      <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
        {Array.from({ length: rows * columns }).map((_, index) => (
          <input
            key={index}
            aria-label={`${label} row ${Math.floor(index / columns) + 1} column ${(index % columns) + 1}`}
            type="number"
            value={Number.isFinite(values[index]) ? values[index] : 0}
            disabled={disabled}
            onChange={(event) => {
              const next = [...values];
              next[index] = Number(event.target.value);
              onChange(next);
            }}
            className="min-h-10 rounded-lg border border-slate-700 bg-slate-950 px-2 text-center text-sm font-bold text-white"
          />
        ))}
      </div>
    </fieldset>
  );
}

export function FormulaBuilder({
  label,
  value,
  disabled,
  placeholder,
  onChange,
}: {
  label: string;
  value: string;
  disabled?: boolean;
  placeholder?: string;
  onChange(value: string): void;
}) {
  return (
    <label className="block rounded-xl border border-slate-800 bg-slate-950/55 p-3 text-xs font-bold text-slate-300">
      {label}
      <input
        type="text"
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        className="mt-2 min-h-11 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 font-mono text-sm text-white"
      />
    </label>
  );
}

export function SimulationControls({
  running,
  disabled,
  onSubmit,
  onReset,
  submitLabel = "Submit and simulate",
}: {
  running?: boolean;
  disabled?: boolean;
  onSubmit(): void;
  onReset?(): void;
  submitLabel?: string;
}) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row">
      <Button className="flex-1" onClick={onSubmit} disabled={disabled || running}>
        <Icon name="Play" size={18} />
        {running ? "Simulating result..." : submitLabel}
      </Button>
      {onReset && (
        <Button type="button" variant="secondary" onClick={onReset} disabled={disabled || running}>
          Reset
        </Button>
      )}
    </div>
  );
}

export function PuzzleFeedbackLayer({
  feedback,
  running,
}: {
  feedback?: ValidationResult | null;
  running?: boolean;
}) {
  if (running) {
    return (
      <div className="rounded-xl border border-amber-300/25 bg-amber-400/10 p-3 text-sm font-bold text-amber-200" role="status">
        Simulation running...
      </div>
    );
  }
  if (!feedback) {
    return (
      <div className="rounded-xl border border-slate-800 bg-slate-950/55 p-3 text-sm text-slate-400" role="status">
        Adjust the controls, then submit when the puzzle state matches your answer.
      </div>
    );
  }
  return (
    <div
      className={clsx(
        "rounded-xl border p-3 text-sm font-bold",
        feedback.success
          ? "border-emerald-300/30 bg-emerald-400/10 text-emerald-200"
          : "border-amber-300/30 bg-amber-400/10 text-amber-200",
      )}
      role="status"
    >
      {feedback.feedback}
      <span className="ml-2 text-xs opacity-75">Score {feedback.score}</span>
    </div>
  );
}

export function PuzzleHintPanel({
  hints,
  visualConfig,
}: {
  hints: string[];
  visualConfig?: Record<string, unknown>;
}) {
  const flags = getVisualFlags(visualConfig);
  if (!flags.showHints || hints.length === 0) return null;
  return (
    <details className="rounded-xl border border-slate-800 bg-slate-950/55 p-3">
      <summary className="cursor-pointer text-sm font-bold text-slate-200">
        Hints <span className="text-xs text-slate-500">({hints.length})</span>
      </summary>
      <div className="mt-3 grid gap-2">
        {hints.map((hint, index) => (
          <div key={`${hint}-${index}`} className="rounded-lg border border-slate-800 bg-slate-950 p-3 text-xs leading-5 text-slate-400">
            <span className="font-bold text-slate-300">Hint {index + 1}: </span>
            {hint}
          </div>
        ))}
      </div>
    </details>
  );
}

export function PuzzleStat({
  label,
  value,
  tone = "slate",
}: {
  label: string;
  value: ReactNode;
  tone?: "cyan" | "emerald" | "amber" | "rose" | "slate";
}) {
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-950/60 p-3">
      <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{label}</p>
      <div className="mt-1 flex items-center gap-2 text-lg font-extrabold text-white">
        <Pill tone={tone}>{value}</Pill>
      </div>
    </div>
  );
}

export function NumberInput({
  label,
  value,
  disabled,
  unit,
  step = 1,
  onChange,
}: {
  label: string;
  value: unknown;
  disabled?: boolean;
  unit?: string;
  step?: number;
  onChange(value: number | ""): void;
}) {
  return (
    <label className="block text-xs font-bold text-slate-300">
      {label}
      <div className="mt-2 flex items-center gap-2">
        <input
          type="number"
          value={typeof value === "number" || typeof value === "string" ? value : ""}
          step={step}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value === "" ? "" : Number(event.target.value))}
          className="min-h-11 w-full rounded-xl border border-slate-700 bg-slate-950 px-3 text-sm text-white"
        />
        {unit && <span className="text-xs font-bold text-slate-500">{unit}</span>}
      </div>
    </label>
  );
}

export function BooleanChoice({
  label,
  value,
  disabled,
  onChange,
}: {
  label: string;
  value: unknown;
  disabled?: boolean;
  onChange(value: boolean): void;
}) {
  return (
    <fieldset className="rounded-xl border border-slate-800 bg-slate-950/55 p-3">
      <legend className="px-1 text-xs font-bold text-slate-300">{label}</legend>
      <div className="grid grid-cols-2 gap-2">
        {[true, false].map((option) => (
          <button
            key={String(option)}
            type="button"
            disabled={disabled}
            aria-pressed={value === option}
            onClick={() => onChange(option)}
            className={clsx(
              "min-h-10 rounded-lg border px-3 text-sm font-bold transition",
              value === option
                ? "border-cyan-300 bg-cyan-300/15 text-cyan-100"
                : "border-slate-700 bg-slate-950 text-slate-300 hover:border-slate-500",
            )}
          >
            {option ? "Yes" : "No"}
          </button>
        ))}
      </div>
    </fieldset>
  );
}

function GridLines({
  bounds,
  projection,
  showLabels,
}: {
  bounds: GridBounds;
  projection: GridProjection;
  showLabels: boolean;
}) {
  const xStart = Math.ceil(bounds.xMin);
  const xEnd = Math.floor(bounds.xMax);
  const yStart = Math.ceil(bounds.yMin);
  const yEnd = Math.floor(bounds.yMax);
  return (
    <g>
      {Array.from({ length: xEnd - xStart + 1 }, (_, index) => xStart + index).map((x) => {
        const top = projection.project({ x, y: bounds.yMax });
        const bottom = projection.project({ x, y: bounds.yMin });
        return <line key={`x-${x}`} x1={top.x} y1={top.y} x2={bottom.x} y2={bottom.y} className="stroke-slate-800" strokeWidth="0.35" />;
      })}
      {Array.from({ length: yEnd - yStart + 1 }, (_, index) => yStart + index).map((y) => {
        const left = projection.project({ x: bounds.xMin, y });
        const right = projection.project({ x: bounds.xMax, y });
        return <line key={`y-${y}`} x1={left.x} y1={left.y} x2={right.x} y2={right.y} className="stroke-slate-800" strokeWidth="0.35" />;
      })}
      {showLabels && (
        <>
          <text x="4" y="96" className="fill-slate-500" fontSize="3.4">
            {bounds.xMin},{bounds.yMin}
          </text>
          <text x="78" y="8" className="fill-slate-500" fontSize="3.4">
            {bounds.xMax},{bounds.yMax}
          </text>
        </>
      )}
    </g>
  );
}

function ShapeItem({
  shape,
  projection,
}: {
  shape: ShapeDefinition;
  projection: GridProjection;
}) {
  const tone = shape.tone ?? "cyan";
  const classes = shapeClasses(tone);
  if (shape.type === "circle") {
    const center = projection.project(shape.center);
    const edge = projection.project({ x: shape.center.x + shape.radius, y: shape.center.y });
    return (
      <g>
        <circle cx={center.x} cy={center.y} r={Math.abs(edge.x - center.x)} className={classes} strokeWidth="0.8" />
        {shape.label && <ShapeLabel point={center} label={shape.label} />}
      </g>
    );
  }
  if (shape.type === "box") {
    const topLeft = projection.project({ x: shape.minX, y: shape.maxY });
    const bottomRight = projection.project({ x: shape.maxX, y: shape.minY });
    return (
      <g>
        <rect x={topLeft.x} y={topLeft.y} width={bottomRight.x - topLeft.x} height={bottomRight.y - topLeft.y} className={classes} strokeWidth="0.8" />
        {shape.label && <ShapeLabel point={topLeft} label={shape.label} />}
      </g>
    );
  }
  if (shape.type === "line") {
    const from = projection.project(shape.from);
    const to = projection.project(shape.to);
    return (
      <g>
        <line x1={from.x} y1={from.y} x2={to.x} y2={to.y} className={classes} strokeWidth="1.1" />
        {shape.label && <ShapeLabel point={to} label={shape.label} />}
      </g>
    );
  }
  return (
    <g>
      <polyline points={shape.points.map((point) => `${projection.project(point).x},${projection.project(point).y}`).join(" ")} className={classes} strokeWidth="1.1" fill="none" />
      {shape.label && shape.points.length > 0 && <ShapeLabel point={projection.project(shape.points[shape.points.length - 1])} label={shape.label} />}
    </g>
  );
}

function ShapeLabel({ point, label }: { point: Point; label: string }) {
  return (
    <text x={point.x + 1.8} y={point.y - 1.8} className="fill-white" fontSize="3.5" fontWeight="800">
      {label}
    </text>
  );
}

function shapeClasses(tone: ShapeTone) {
  const classes: Record<ShapeTone, string> = {
    cyan: "fill-cyan-300/15 stroke-cyan-300",
    emerald: "fill-emerald-300/15 stroke-emerald-300",
    amber: "fill-amber-300/15 stroke-amber-300",
    rose: "fill-rose-300/15 stroke-rose-300",
    violet: "fill-violet-300/15 stroke-violet-300",
    slate: "fill-slate-300/10 stroke-slate-300",
  };
  return classes[tone];
}

function polarPoint(origin: Point, radius: number, degrees: number): Point {
  const radians = (degrees * Math.PI) / 180;
  return { x: origin.x + Math.cos(radians) * radius, y: origin.y + Math.sin(radians) * radius };
}

function createProjection(bounds: GridBounds): GridProjection {
  const xRange = Math.max(1, bounds.xMax - bounds.xMin);
  const yRange = Math.max(1, bounds.yMax - bounds.yMin);
  const project = (point: Point): Point => ({
    x: padding + ((point.x - bounds.xMin) / xRange) * plotSize,
    y: padding + ((bounds.yMax - point.y) / yRange) * plotSize,
  });
  const unproject = (point: Point): Point => ({
    x: bounds.xMin + ((point.x - padding) / plotSize) * xRange,
    y: bounds.yMax - ((point.y - padding) / plotSize) * yRange,
  });
  const clampPoint = (point: Point): Point => ({
    x: Math.max(bounds.xMin, Math.min(bounds.xMax, point.x)),
    y: Math.max(bounds.yMin, Math.min(bounds.yMax, point.y)),
  });
  return {
    bounds,
    project,
    unproject,
    clamp: clampPoint,
    toPath(points: Point[]) {
      return points.map((point) => {
        const projected = project(point);
        return `${projected.x},${projected.y}`;
      }).join(" ");
    },
  };
}

export function SvgText(props: SVGProps<SVGTextElement>) {
  return <text fontSize="3.6" fontWeight="800" className="fill-slate-100" {...props} />;
}
